import type { FC, ReactNode } from "react";

/**
 * A single selectable option rendered by {@link CheckBoxList}.
 *
 * `value` is either a primitive key or a tuple whose first element is the key
 * (used by the `actual_status` grouping case in the underlying component).
 * Extra fields are forwarded to the underlying `CheckBox`.
 */
export interface CheckBoxListOption {
  value: string | number | (string | number)[];
  label?: ReactNode;
  disabled?: boolean;
  [key: string]: unknown;
}

/** Error descriptor consumed by the underlying `FormFieldError`. */
export interface CheckBoxListError {
  message?: ReactNode;
  type?: string;
  placement?: string;
}

/**
 * The selected values. The runtime component only ever reads entries via
 * `Array.prototype.includes`/`filter`/lodash `union`/`difference`, all of which
 * tolerate `undefined`/`null` holes, so callers may pass sparse arrays (e.g.
 * `Partial<T>[]` mapped to an optional field).
 */
export type CheckBoxListValue = (string | number | undefined | null)[];

export interface CheckBoxListProps {
  options?: CheckBoxListOption[];
  value?: CheckBoxListValue;
  /**
   * Declared method-style (bivariant params) so consumers can supply a narrower
   * callback (e.g. `(val: string[]) => void`) when they control the option set.
   * The runtime always invokes this with an array derived from `value`/option
   * values.
   */
  onChange(value: CheckBoxListValue): void;
  label?: ReactNode;
  disabled?: boolean;
  inDropdown?: boolean;
  withBorder?: boolean;
  description?: ReactNode;
  labelTop?: boolean;
  required?: boolean;
  hideLabelHelper?: boolean;
  noMargin?: boolean;
  sortable?: boolean;
  direction?: "row" | "column";
  dateSelector?: boolean;
  // The runtime only does `error && <FormFieldError .../>`, so `null` is a
  // valid "no error" value alongside `undefined`.
  error?: CheckBoxListError | null;
  className?: string;
  withBg?: boolean;
  "data-testid"?: string;
  loading?: boolean;
  alignLeft?: boolean;
  searchable?: boolean;
  textToHighlight?: string;
  emptyState?: ReactNode;
  readOnly?: boolean;
  hasSelectAll?: boolean;
  selectAllLabel?: ReactNode;
}

// The runtime `CheckBoxList` export exists in `@SeemplicityDev/ticket-master`'s
// bundle but is not declared in its shipped `index.d.ts`. Augment the module to
// surface the export with proper types instead of falling back to `any`.
// TODO: remove this augmentation once `@SeemplicityDev/ticket-master` exports
// `CheckBoxList` (and its props) from its type declarations.
declare module "@SeemplicityDev/ticket-master" {
  export const CheckBoxList: FC<CheckBoxListProps>;
}

// eslint-disable-next-line import/first -- import must follow the module augmentation above
import { CheckBoxList } from "@SeemplicityDev/ticket-master";

export default CheckBoxList;
